import { useRef, useState } from 'react';
import { AlertTriangle, Mic, Square, Wand2 } from 'lucide-react';
import type { ExtractionResult, Instrument } from '../lib/types';
import { tidyTranscript } from '../hooks/useSpeech';
import { useRecorder } from '../hooks/useRecorder';
import { uploadRecording } from '../services/storage';
import { useAuth } from '../hooks/useAuth';
import { runExtraction, transcribeRecording } from '../services/extract';
import { runReaderExtraction } from '../services/readings';
import type { ReaderExtraction } from '../services/readings';
import { setCaptureFailed, setExtraction } from '../services/captures';
import { updateDoc } from 'firebase/firestore';
import { captureDoc } from '../services/captures';
import { Button, cx } from './ui';

/**
 * A spoken note attached to a photograph, and a re-read using both.
 *
 * The person holding the camera knows things the image does not carry — that
 * the third row is a repeat, what an abbreviated name stands for, that only the
 * highlighted rows matter. Saying so and re-reading is faster and less error-prone than
 * correcting a dozen fields by hand, and the note is kept with the capture, so
 * the reasoning survives alongside the result.
 */
export default function PhotoNote({
  projectId,
  captureId,
  storagePath,
  instrument,
  existingNote,
  firstRead = false,
  readerId,
  onReaderExtracted,
  onReExtracted,
  onFailed,
  onBusyChange,
}: {
  projectId: string;
  captureId: string;
  storagePath: string | null;
  instrument: Instrument;
  existingNote: string | null;
  /**
   * The photo has never been read. The panel opens straight away and an empty
   * note is allowed, so this is both "add a note" and "read it" — the note only
   * reaches the first extraction if it is written before that extraction runs.
   */
  firstRead?: boolean;
  /** When set, the capture is read against this reader rather than the old fixed fields. */
  readerId?: string | null;
  onReaderExtracted?: (result: ReaderExtraction, note: string) => void;
  onReExtracted: (result: ExtractionResult, note: string) => void;
  onFailed?: (message: string) => void;
  onBusyChange?: (busy: boolean) => void;
}) {
  const recorder = useRecorder();
  const { user } = useAuth();
  const [open, setOpen] = useState(firstRead);
  const [spoken, setSpoken] = useState('');
  const [transcribing, setTranscribing] = useState(false);
  const stoppingRef = useRef(false);

  /**
   * The same recorder the voice screen uses. A note is dictated exactly the way
   * a reading is: it records until stopped, and the words are worked out after.
   */
  async function stopAndTranscribe() {
    // Without this, a double tap appends the same note twice.
    if (stoppingRef.current) return;
    stoppingRef.current = true;
    const rec = await recorder.stop();
    if (!rec) {
      stoppingRef.current = false;
      return;
    }
    setTranscribing(true);
    try {
      const path = await uploadRecording(user?.uid ?? '', `${captureId}-note`, rec.blob, rec.mimeType);
      const text = await transcribeRecording(projectId, path, rec.mimeType);
      setSpoken((prev) => `${prev} ${text}`.trim());
      setEdited(null);
    } catch (e) {
      setError((e as Error)?.message ?? 'Could not transcribe that.');
    } finally {
      setTranscribing(false);
      stoppingRef.current = false;
    }
  }
  const [edited, setEdited] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const note = edited ?? spoken;
  // A re-read with no note would just repeat the reading that is already there.
  // A first read with no note is the ordinary case.
  const canReRead =
    !!storagePath && !recorder.recording && !transcribing && (firstRead || note.trim().length > 0);

  function setBusyBoth(v: boolean) {
    setBusy(v);
    onBusyChange?.(v);
  }

  async function reRead() {
    if (!storagePath || !canReRead) return;
    const text = tidyTranscript(note);
    setBusyBoth(true);
    setError(null);
    try {
      if (readerId) {
        // The reader path returns a different shape entirely — per-reading
        // records against user-defined fields — so it has its own callback.
        const result = await runReaderExtraction({
          projectId,
          captureId,
          readerId,
          storagePath,
          transcript: text,
        });
        if (text) await updateDoc(captureDoc(projectId, captureId), { transcript: text });
        onReaderExtracted?.(result, text);
        setOpen(false);
        setSpoken('');
        setEdited(null);
        return;
      }

      const result = await runExtraction({
        projectId,
        captureId,
        mode: 'photo',
        storagePath,
        transcript: text,
        instrument,
      });
      await setExtraction(projectId, captureId, result);
      // The note is part of the audit trail: it explains why the numbers landed
      // where they did. An empty first read has no note to record.
      if (text) await updateDoc(captureDoc(projectId, captureId), { transcript: text });
      onReExtracted(result, text);
      setOpen(false);
      setSpoken('');
      setEdited(null);
    } catch (e) {
      const message = (e as Error)?.message ?? 'Could not read the photo.';
      setError(message);
      // A failed first read must not strand the capture: record it so the
      // history list shows why, and so manual entry is still reachable.
      if (firstRead) await setCaptureFailed(projectId, captureId, message).catch(() => {});
      onFailed?.(message);
    } finally {
      setBusyBoth(false);
    }
  }

  if (!open) {
    return (
      <div className="mb-4">
        <Button
          variant="secondary"
          size="sm"
          icon={<Mic size={14} />}
          onClick={() => setOpen(true)}
          disabled={!storagePath}
        >
          {existingNote ? 'Add another note' : 'Add a note — type or speak'}
        </Button>
        {existingNote && (
          <p className="mt-2 rounded-lg border border-ink-700 bg-ink-800/40 p-2.5 font-mono text-xs text-ink-300">
            {existingNote}
          </p>
        )}
        {!storagePath && (
          <p className="mt-1.5 text-xs text-ink-400">
            The photo has not finished uploading, so it cannot be read again yet.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="mb-4 rounded-xl border border-accent-500/50 bg-accent-500/5 p-3">
      <p className="text-sm font-medium text-ink-100">
        {firstRead ? 'Add a note, or just read it' : 'Tell it what to look at'}
      </p>
      <p className="mt-0.5 text-xs text-ink-400">
        {firstRead
          ? 'Optional — speak or type anything the photo does not show, then read.'
          : 'Speak or type anything the photo does not show.'}
      </p>

      <div
        className={cx(
          'mt-3 min-h-16 rounded-lg border p-3 text-sm',
          recorder.recording ? 'border-accent-500 bg-ink-900' : 'border-ink-700 bg-ink-800',
        )}
      >
        {recorder.recording && (
          <div className="mb-2 flex items-center gap-2 text-xs font-medium text-accent-400">
            <span className="relative flex size-2">
              <span className="absolute inline-flex size-full animate-ping rounded-full bg-accent-400 opacity-75" />
              <span className="relative inline-flex size-2 rounded-full bg-accent-500" />
            </span>
            Recording {Math.floor(recorder.seconds / 60)}:
            {String(recorder.seconds % 60).padStart(2, '0')} — pauses are fine, press Stop when done
          </div>
        )}
        {note ? (
          <p className="font-mono leading-relaxed text-ink-100">{note}</p>
        ) : (
          <p className="text-ink-400">
            {recorder.recording
              ? 'Listening — take as long as you need.'
              : transcribing
                ? 'Working out what you said…'
                : 'Press Record, or type in the box below.'}
          </p>
        )}
      </div>

      {/* Always available, not only once speech has produced something. A lab is
          often too loud to dictate in, and a browser without speech recognition
          would otherwise have no way to leave a note at all. */}
      {!recorder.recording && (
        <textarea
          rows={2}
          value={note}
          placeholder="Or type a note"
          onChange={(e) => setEdited(e.target.value)}
          className="mt-2 w-full rounded-lg border border-ink-600 bg-ink-800 px-3 py-2 font-mono text-sm text-ink-100 placeholder:font-sans placeholder:text-ink-500 focus:border-accent-500 focus:outline-none"
        />
      )}

      {recorder.error && (
        <p className="mt-2 flex gap-1.5 text-xs text-amber-300">
          <AlertTriangle size={12} className="mt-0.5 shrink-0" />
          {recorder.error}
        </p>
      )}
      {error && (
        <p className="mt-2 rounded-lg border border-red-800 bg-red-950 p-2 text-xs text-red-200">
          {error}
        </p>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        {recorder.recording ? (
          <Button
            variant="danger"
            size="sm"
            icon={<Square size={13} />}
            onClick={() => void stopAndTranscribe()}
          >
            Stop
          </Button>
        ) : (
          <Button
            variant="secondary"
            size="sm"
            icon={<Mic size={13} />}
            loading={transcribing}
            onClick={() => void recorder.start()}
            disabled={busy || transcribing || !recorder.supported}
          >
            {note ? 'Record again' : 'Record a note'}
          </Button>
        )}
        <Button
          variant="primary"
          size="sm"
          icon={<Wand2 size={13} />}
          loading={busy}
          disabled={!canReRead}
          onClick={() => void reRead()}
        >
          {firstRead ? (note.trim() ? 'Read the photo with this note' : 'Read the photo') : 'Read the photo again'}
        </Button>
        {/* Before the first read there is nothing to go back to — the screen
            behind this panel has no values in it yet. */}
        {!firstRead && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              recorder.cancel();
              setSpoken('');
              setEdited(null);
              setOpen(false);
            }}
            disabled={busy}
          >
            Cancel
          </Button>
        )}
      </div>

      {!recorder.supported && (
        <p className="mt-2 text-xs text-ink-400">
          This browser cannot record audio. Typing the note works the same way.
        </p>
      )}
    </div>
  );
}
